/**
 * IntellioInsightCharts (§5.8)
 *
 * Chart block using recharts for compact confidence/risk trends,
 * recommendation distribution, and reward/penalty/HITL outcome breakdown.
 *
 * Charts support the workflow; they do not become a dashboard circus.
 * Empty states must be explicit and calm.
 *
 * @module intellio-v3/IntellioInsightCharts
 */
import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import type { IntellioChartPanel } from "./types.js";

export interface IntellioInsightChartsProps {
  charts: IntellioChartPanel[];
  panelHeight?: number;
  layoutMode?: "compact" | "comfortable";
  maxPanelWidth?: number;
  panelChrome?: "card" | "bare";
}

type InsightChartLayoutMode = NonNullable<
  IntellioInsightChartsProps["layoutMode"]
>;

type InsightChartAxisLayout = {
  axisHeight: number;
  bottomMargin: number;
  labelLinesByValue: Map<string, string[]>;
  maxLineCount: number;
  tickFontSize: number;
  tickLineHeight: number;
  tickStartDy: number;
  yAxisTickFontSize: number;
  yAxisWidth: number;
};

const CHART_AXIS_TEXT_COLOR = "var(--intellio-chart-axis-color, #64748b)";
const CHART_GRID_COLOR = "var(--intellio-chart-grid-color, #e5e7eb)";
const CHART_TOOLTIP_BG = "var(--intellio-chart-tooltip-bg, #ffffff)";
const CHART_TOOLTIP_BORDER = "var(--intellio-chart-tooltip-border, #e5e7eb)";
const CHART_TOOLTIP_TEXT = "var(--intellio-chart-tooltip-text, #243235)";

const normalizeChartTickLabel = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s*\/\s*/g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ellipsizeChartTick = (value: string, maxChars: number): string => {
  if (value.length <= maxChars) {
    return value;
  }
  return `${value.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
};

const resolveChartTickWrapSettings = (
  categoryCount: number,
  layoutMode: InsightChartLayoutMode,
): {
  maxCharsPerLine: number;
  maxLines: number;
  softTokenLimit: number;
} => {
  if (layoutMode === "comfortable") {
    if (categoryCount >= 8) {
      return { maxCharsPerLine: 10, maxLines: 2, softTokenLimit: 12 };
    }
    if (categoryCount >= 6) {
      return { maxCharsPerLine: 12, maxLines: 2, softTokenLimit: 14 };
    }
    return { maxCharsPerLine: 18, maxLines: 3, softTokenLimit: 22 };
  }
  if (categoryCount >= 8) {
    return { maxCharsPerLine: 7, maxLines: 3, softTokenLimit: 9 };
  }
  if (categoryCount >= 6) {
    return { maxCharsPerLine: 9, maxLines: 2, softTokenLimit: 11 };
  }
  return { maxCharsPerLine: 12, maxLines: 2, softTokenLimit: 14 };
};

export const wrapInsightChartTickLabel = (
  value: unknown,
  options: {
    maxCharsPerLine?: number;
    maxLines?: number;
    softTokenLimit?: number;
  } = {},
): string[] => {
  const normalized = normalizeChartTickLabel(value);
  if (!normalized) {
    return [""];
  }

  const maxCharsPerLine = Math.max(4, options.maxCharsPerLine ?? 10);
  const maxLines = Math.max(1, options.maxLines ?? 2);
  const softTokenLimit = Math.max(
    maxCharsPerLine,
    options.softTokenLimit ?? maxCharsPerLine + 2,
  );
  const tokens = normalized
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) =>
      token.length <= softTokenLimit
        ? token
        : ellipsizeChartTick(token, softTokenLimit),
    );

  const lines: string[] = [];
  let currentLine = "";

  for (const token of tokens) {
    const nextLine = currentLine ? `${currentLine} ${token}` : token;
    if (currentLine && nextLine.length > maxCharsPerLine) {
      lines.push(currentLine);
      currentLine = token;
      continue;
    }
    currentLine = nextLine;
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, Math.max(0, maxLines - 1));
  const remainder = lines.slice(Math.max(0, maxLines - 1)).join(" ");
  visibleLines.push(ellipsizeChartTick(remainder, softTokenLimit));
  return visibleLines;
};

export const buildInsightChartAxisLayout = (
  panel: IntellioChartPanel,
  layoutMode: InsightChartLayoutMode = "compact",
): InsightChartAxisLayout => {
  const categoryCount = Math.max(panel.data.length, 1);
  const wrapSettings = resolveChartTickWrapSettings(categoryCount, layoutMode);
  const labelLinesByValue = new Map<string, string[]>();
  const tickFontSize = layoutMode === "comfortable" ? 11 : 9;
  const tickLineHeight = layoutMode === "comfortable" ? 13 : 11;
  const tickStartDy = layoutMode === "comfortable" ? 10 : 10;
  const yAxisTickFontSize = layoutMode === "comfortable" ? 11 : 10;
  const yAxisWidth = layoutMode === "comfortable" ? 48 : 40;

  panel.data.forEach((row) => {
    const rawLabel = row[panel.xKey];
    const normalizedLabel = normalizeChartTickLabel(rawLabel);
    if (!normalizedLabel || labelLinesByValue.has(normalizedLabel)) {
      return;
    }
    labelLinesByValue.set(
      normalizedLabel,
      wrapInsightChartTickLabel(normalizedLabel, wrapSettings),
    );
  });

  const maxLineCount = Math.max(
    1,
    ...Array.from(labelLinesByValue.values()).map((lines) => lines.length),
  );

  return {
    axisHeight:
      (layoutMode === "comfortable" ? 30 : 16) + maxLineCount * tickLineHeight,
    bottomMargin:
      (layoutMode === "comfortable" ? 24 : 10) + maxLineCount * tickLineHeight,
    labelLinesByValue,
    maxLineCount,
    tickFontSize,
    tickLineHeight,
    tickStartDy,
    yAxisTickFontSize,
    yAxisWidth,
  };
};

const WrappedChartXAxisTick: React.FC<{
  labelLinesByValue: Map<string, string[]>;
  tickFontSize: number;
  tickLineHeight: number;
  tickStartDy: number;
  x?: number;
  y?: number;
  payload?: { value?: unknown };
}> = ({
  labelLinesByValue,
  tickFontSize,
  tickLineHeight,
  tickStartDy,
  x = 0,
  y = 0,
  payload,
}) => {
  const normalizedLabel = normalizeChartTickLabel(payload?.value);
  const lines = labelLinesByValue.get(normalizedLabel) ?? [
    normalizedLabel || "",
  ];

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={tickStartDy}
        textAnchor="middle"
        fill={CHART_AXIS_TEXT_COLOR}
        fontSize={tickFontSize}
        fontWeight={600}
      >
        {lines.map((line, index) => (
          <tspan
            key={`${normalizedLabel || "tick"}-${index + 1}`}
            x={0}
            dy={index === 0 ? 0 : tickLineHeight}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const ChartRenderer: React.FC<{
  panel: IntellioChartPanel;
  panelHeight: number;
  layoutMode: InsightChartLayoutMode;
}> = ({ panel, panelHeight, layoutMode }) => {
  if (!panel.data || panel.data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-xs text-irongrey/40">
        No data available
      </div>
    );
  }

  const axisLayout = buildInsightChartAxisLayout(panel, layoutMode);
  const commonProps = {
    data: panel.data as Record<string, unknown>[],
    margin: {
      top: layoutMode === "comfortable" ? 8 : 4,
      right: layoutMode === "comfortable" ? 16 : 8,
      bottom: axisLayout.bottomMargin,
      left: layoutMode === "comfortable" ? -4 : -20,
    },
  };

  const axisProps = {
    xAxis: (
      <XAxis
        dataKey={panel.xKey}
        tick={
          <WrappedChartXAxisTick
            labelLinesByValue={axisLayout.labelLinesByValue}
            tickFontSize={axisLayout.tickFontSize}
            tickLineHeight={axisLayout.tickLineHeight}
            tickStartDy={axisLayout.tickStartDy}
          />
        }
        interval={0}
        height={axisLayout.axisHeight}
        axisLine={false}
        tickLine={false}
      />
    ),
    yAxis: (
      <YAxis
        tick={{
          fontSize: axisLayout.yAxisTickFontSize,
          fill: CHART_AXIS_TEXT_COLOR,
        }}
        axisLine={false}
        tickLine={false}
        width={axisLayout.yAxisWidth}
      />
    ),
    grid: <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />,
    tooltip: (
      <Tooltip
        contentStyle={{
          fontSize: 11,
          borderRadius: 8,
          backgroundColor: CHART_TOOLTIP_BG,
          border: `1px solid ${CHART_TOOLTIP_BORDER}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          color: CHART_TOOLTIP_TEXT,
        }}
        itemStyle={{ color: CHART_TOOLTIP_TEXT }}
        labelStyle={{ color: CHART_TOOLTIP_TEXT }}
      />
    ),
  };

  switch (panel.kind) {
    case "bar":
    case "stacked-bar":
      return (
        <ResponsiveContainer width="100%" height={panelHeight}>
          <BarChart
            {...commonProps}
            barCategoryGap={layoutMode === "comfortable" ? "18%" : "12%"}
          >
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {panel.series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                fill={s.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={layoutMode === "comfortable" ? 80 : 52}
                stackId={panel.kind === "stacked-bar" ? "stack" : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "line":
      return (
        <ResponsiveContainer width="100%" height={panelHeight}>
          <LineChart {...commonProps}>
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {panel.series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 3, fill: s.color }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case "area":
      return (
        <ResponsiveContainer width="100%" height={panelHeight}>
          <AreaChart {...commonProps}>
            {axisProps.grid}
            {axisProps.xAxis}
            {axisProps.yAxis}
            {axisProps.tooltip}
            {panel.series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    default:
      return null;
  }
};

export const IntellioInsightCharts: React.FC<IntellioInsightChartsProps> = ({
  charts,
  panelHeight,
  layoutMode = "compact",
  maxPanelWidth,
  panelChrome = "card",
}) => {
  if (!charts || charts.length === 0) {
    return (
      <div
        data-testid="intellio-insight-charts"
        className="rounded-xl border border-gray-100 bg-white p-4 font-[Albert_Sans]"
      >
        <p className="text-xs text-irongrey/40">No insight charts available</p>
      </div>
    );
  }

  const resolvedPanelHeight =
    panelHeight ?? (layoutMode === "comfortable" ? 260 : 140);
  const resolvedMaxPanelWidth =
    maxPanelWidth ?? (layoutMode === "comfortable" ? 980 : undefined);
  const chartCanvasStyle = resolvedMaxPanelWidth
    ? { maxWidth: `${resolvedMaxPanelWidth}px` }
    : undefined;

  return (
    <div
      data-testid="intellio-insight-charts"
      className="space-y-3 font-[Albert_Sans]"
    >
      {charts.map((panel) => (
        <div
          key={panel.id}
          data-testid={`intellio-chart-${panel.id}`}
          className={
            panelChrome === "bare"
              ? "bg-transparent p-0"
              : "rounded-xl border border-gray-100 bg-white p-4 shadow-card"
          }
        >
          <h4
            className={`${layoutMode === "comfortable" ? "text-[14px]" : "text-xs"} font-semibold text-irongrey font-[Mulish]`}
          >
            {panel.title}
          </h4>
          {panel.subtitle && (
            <p
              className={`mt-0.5 ${layoutMode === "comfortable" ? "text-[12px] leading-5" : "text-[11px]"} text-irongrey/50`}
            >
              {panel.subtitle}
            </p>
          )}
          <div
            className={`${layoutMode === "comfortable" ? "mt-3 flex justify-center" : "mt-2"}`}
          >
            <div
              className="w-full"
              style={chartCanvasStyle}
              data-testid={`intellio-chart-canvas-${panel.id}`}
            >
              <ChartRenderer
                panel={panel}
                panelHeight={resolvedPanelHeight}
                layoutMode={layoutMode}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
